import { MapConfig, StageGoal } from '../sim/world'

export interface MapDef extends MapConfig {
  blurb: string
  defaultUnlocked: boolean
  unlockHint: string
  /** Difficulty/spawn-rate multiplier baked into the map identity. */
  intensity: number
  /** Terrain elevation amplitude (world units). 0 = flat. */
  elevation: number
  /** Highest replayable tier (default 3). Higher tiers chain more stages. */
  maxTier?: number
}

export const MAP_LIST: MapDef[] = [
  {
    id: 'meadow',
    name: 'Tranquility Flats',
    blurb: 'Dusty grey lunar plains. A calm place to learn to bonk aliens.',
    groundColor: 0x3a3a4a,
    fogColor: 0x0a0618,
    accentColor: 0x1fe3d0,
    radius: 60,
    surviveSeconds: 600,
    goalSeconds: 300, // ~5-min arc: ramp → 2 scheduled bosses (120s/270s) → power spike → portal → final boss

    enemyPool: ['grub', 'runt', 'brute', 'swarmling', 'dasher', 'blob', 'ghoul', 'scuttle', 'locust', 'roomba', 'razorwing', 'warchanter'],
    bossPool: ['boss_golem', 'boss_hydra', 'boss_forklift', 'boss_rootzilla', 'boss_lich'], // ascending by HP (spawnBoss/spawnFinalBoss assume weakest→toughest)
    intensity: 1,
    elevation: 8,
    // gentle, broad lunar rolls — continent-dominant, low high-freq → cliff-free
    terrain: { continentFreq: 0.011, continentWeight: 1.5, ridgeWeight: 0.25, midFreq: 0.05, midWeight: 0.2, detailWeight: 0.1 },
    defaultUnlocked: true,
    unlockHint: 'Available from the start.',
  },
  {
    id: 'dunes',
    name: 'The Rust Dunes',
    blurb: 'Endless rust-red Martian dunes and things that bite. Bring oxygen.',
    groundColor: 0x8c3b1e,
    fogColor: 0x3a1208,
    accentColor: 0xff7a18,
    radius: 65,
    surviveSeconds: 600,
    goalSeconds: 300, // ~5-min arc: ramp → 2 scheduled bosses (120s/270s) → power spike → portal → final boss

    enemyPool: ['grub', 'runt', 'swarmling', 'dasher', 'archer', 'bomber', 'brute', 'sniper', 'stalker', 'scuttle', 'boar', 'magmabrute', 'locust', 'razorwing', 'flankwraith', 'aegis', 'warchanter'],
    bossPool: ['boss_golem', 'boss_hydra', 'boss_lich', 'boss_sandwyrm', 'boss_scorpion', 'boss_warlord'], // ascending by HP
    intensity: 1.25,
    elevation: 8,
    // big smooth dunes — very continent-dominant at very low freq, almost no ridges
    terrain: { continentFreq: 0.009, continentWeight: 1.7, ridgeWeight: 0.2, midFreq: 0.05, midWeight: 0.25, detailWeight: 0.1 },
    defaultUnlocked: false,
    unlockHint: 'Clear Tranquility Flats (defeat its boss).',
  },
  {
    id: 'graveyard',
    name: 'Hulk Drift',
    blurb: 'A drifting wreck of a station. The dead crew will not rest.',
    groundColor: 0x2e3640,
    fogColor: 0x06141c,
    accentColor: 0x5be0c8,
    radius: 60,
    surviveSeconds: 600,
    goalSeconds: 300, // ~5-min arc: ramp → 2 scheduled bosses (120s/270s) → power spike → portal → final boss

    enemyPool: ['grub', 'ghoul', 'wisp', 'spitter', 'summoner', 'shielder', 'blob', 'revenant', 'bomber', 'ironlich', 'voodoo', 'batcloud', 'imp', 'aegis', 'warchanter', 'overseer', 'flankwraith', 'razorwing'],
    bossPool: ['boss_hydra', 'boss_lich', 'boss_reaper', 'boss_djskel', 'boss_queen', 'boss_warlord'], // ascending by HP
    intensity: 1.5,
    elevation: 7,
    // broken mesas/plateaus — stronger ridges carve flat-topped steps with steeper edges
    terrain: { continentFreq: 0.013, continentWeight: 1.0, ridgeFreq: 0.045, ridgeWeight: 0.7, midFreq: 0.07, midWeight: 0.35, detailWeight: 0.2 },
    defaultUnlocked: false,
    unlockHint: 'Clear The Rust Dunes.',
  },
  {
    id: 'rift',
    name: 'The Void Maw',
    blurb: 'A tear in spacetime. Only the strongest survive the swarm.',
    groundColor: 0x2a1840,
    fogColor: 0x0a0618,
    accentColor: 0xff2e97,
    radius: 55,
    surviveSeconds: 600,
    goalSeconds: 300, // ~5-min arc: ramp → 2 scheduled bosses (120s/270s) → power spike → portal → final boss

    enemyPool: [
      'runt', 'swarmling', 'dasher', 'brute', 'archer', 'bomber', 'ghoul', 'stalker',
      'shielder', 'summoner', 'crystalback', 'revenant', 'wisp', 'spitter', 'reaver', 'sniper',
      'yetibull', 'bogtroll', 'magmabrute', 'voodoo', 'ironlich', 'boar', 'batcloud', 'scuttle', 'spittoad', 'imp', 'roomba', 'locust',
      'aegis', 'warchanter', 'overseer', 'razorwing', 'flankwraith',
    ],
    bossPool: ['boss_golem', 'boss_hydra', 'boss_lich', 'boss_reaper', 'boss_djskel', 'boss_scorpion', 'boss_fridge', 'boss_queen', 'boss_warlord'], // ascending by HP (was ending on the weakest boss)
    intensity: 2,
    elevation: 12,
    // jagged rift — ridge- and mid-heavy at higher freq → rough, cliffy, the harshest terrain
    terrain: { continentFreq: 0.014, continentWeight: 0.8, ridgeFreq: 0.05, ridgeWeight: 0.9, midFreq: 0.09, midWeight: 0.5, detailWeight: 0.3 },
    defaultUnlocked: false,
    unlockHint: 'Clear Hulk Drift.',
  },
]

export const MAPS: Record<string, MapDef> = {}
for (const m of MAP_LIST) MAPS[m.id] = m

export interface DifficultyDef {
  id: string
  name: string
  mult: number // scales enemy hp + spawn density (world.difficulty multiplier)
  rewardMult: number // scales xp + gold drops
  color: number
  hint: string
}

export const DIFFICULTIES: DifficultyDef[] = [
  { id: 'easy', name: 'Easy', mult: 0.8, rewardMult: 0.9, color: 0x6fc04a, hint: 'Fewer, weaker foes. A relaxed bonk.' },
  { id: 'normal', name: 'Normal', mult: 1, rewardMult: 1, color: 0x4caf50, hint: 'The intended challenge.' },
  { id: 'hard', name: 'Hard', mult: 1.4, rewardMult: 1.35, color: 0xff9f4a, hint: 'Denser, tougher swarms — richer loot.' },
  { id: 'cooked', name: 'Cooked', mult: 1.9, rewardMult: 1.8, color: 0xff4060, hint: 'You will probably die. Best rewards.' },
]
export const DIFFICULTY_MAP: Record<string, DifficultyDef> = {}
for (const d of DIFFICULTIES) DIFFICULTY_MAP[d.id] = d

/**
 * MP6 — choose a stage's pre-portal GOAL. Meadow tier-1 always stays the classic survive run (keeps
 * the first-time experience + the canary clean); other maps / higher tiers sometimes become an ELITE
 * HUNT (kill N elites to open the portal), varying the pacing the way Megabonk's tiers do. Deterministic
 * over the passed rng.
 */
export function pickStageGoal(mapId: string, tier: number, _stage: number, rng: { next(): number }): { goal: StageGoal; target: number } {
  if (mapId === 'meadow' && tier === 1) return { goal: 'survive', target: 0 } // first-time + canary stays on 'survive'
  const r = rng.next() // ONE draw (unchanged count → rng stream byte-identical vs the old single-roll)
  if (r < 0.3) return { goal: 'elite-hunt', target: 3 + Math.floor(tier) }
  if (r < 0.55) return { goal: 'slay', target: 60 + Math.floor(tier) * 25 } // cull the swarm to open the portal
  return { goal: 'survive', target: 0 }
}
